import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import {
  BARRIER_PROVIDER_OPTIONS,
  barrierNeedsBaseUrl,
  barrierNeedsWebhookUrl,
  type BarrierProviderKind,
  type BarrierSettings,
} from "@/lib/barrierLprSettings";
import { Button } from "@/components/ui/Button";
import { FormHint } from "@/pages/stammdaten/schnittstellen/FormHint";

interface TenantSettings {
  barrier: BarrierSettings;
}

interface BarrierIntegrationPanelProps {
  canWrite: boolean;
}

export function BarrierIntegrationPanel({ canWrite }: BarrierIntegrationPanelProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [provider, setProvider] = useState<BarrierProviderKind>("magnetic");
  const [baseUrl, setBaseUrl] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [laneId, setLaneId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [apiSecretConfigured, setApiSecretConfigured] = useState(false);
  const [openOnCheckIn, setOpenOnCheckIn] = useState(true);
  const [revokeOnCheckOut, setRevokeOnCheckOut] = useState(true);
  const [syncWhitelist, setSyncWhitelist] = useState(true);
  const [configured, setConfigured] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const settings = await api.get<TenantSettings>("/api/v1/settings");
      const barrier = settings.barrier;
      setEnabled(barrier.enabled);
      setProvider(barrier.provider);
      setBaseUrl(barrier.base_url ?? "");
      setWebhookUrl(barrier.webhook_url ?? "");
      setLaneId(barrier.lane_id ?? "");
      setApiKey("");
      setApiSecret("");
      setApiKeyConfigured(barrier.api_key_configured);
      setApiSecretConfigured(barrier.api_secret_configured);
      setOpenOnCheckIn(barrier.open_on_check_in);
      setRevokeOnCheckOut(barrier.revoke_on_check_out);
      setSyncWhitelist(barrier.sync_whitelist);
      setConfigured(barrier.configured);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Einstellungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = BARRIER_PROVIDER_OPTIONS.find((p) => p.value === provider);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setSuccess(false);
    setError(null);
    setTestResult(null);
    try {
      const body: Record<string, unknown> = {
        enabled,
        provider,
        base_url: baseUrl.trim() || null,
        webhook_url: webhookUrl.trim() || null,
        lane_id: laneId.trim() || null,
        open_on_check_in: openOnCheckIn,
        revoke_on_check_out: revokeOnCheckOut,
        sync_whitelist: syncWhitelist,
      };
      if (apiKey) body.api_key = apiKey;
      if (apiSecret) body.api_secret = apiSecret;
      const updated = await api.patch<TenantSettings>("/api/v1/settings/barrier", body);
      setApiKey("");
      setApiSecret("");
      setApiKeyConfigured(updated.barrier.api_key_configured);
      setApiSecretConfigured(updated.barrier.api_secret_configured);
      setConfigured(updated.barrier.configured);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Schranke konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  async function onTest() {
    if (!canWrite) return;
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      const result = await api.post<{ ok: boolean; message: string; provider: string }>(
        "/api/v1/settings/barrier/test",
        {},
      );
      setTestResult(`${result.ok ? "OK" : "Fehler"} (${result.provider}): ${result.message}`);
    } catch (err) {
      setTestResult(err instanceof Error ? err.message : "Verbindungstest fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Laden…</p>;
  }

  return (
    <form onSubmit={(e) => void onSave(e)} className="space-y-5">
      <FormHint>
        Anbindung von Schranken- und Zufahrtsanlagen. Bei Check-in wird Kennzeichen/Transponder
        freigeschaltet, bei Check-out gesperrt. Für Magnetic Autocontrol (MGC-Pro/EM01) wird ein
        lokales HTTP-Gateway zur ModBus-Bridge erwartet – Details in{" "}
        <code className="text-xs">docs/barrier-lpr-integrations.md</code>.
      </FormHint>

      <label className="flex items-center gap-3 rounded-xl border border-separator bg-fill-quaternary p-4 text-sm">
        <input
          type="checkbox"
          className="rounded border-separator text-accent focus:ring-accent"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          disabled={!canWrite}
        />
        <span className="font-medium text-label">Schranken-Integration aktivieren</span>
      </label>

      {enabled && configured ? (
        <p className="rounded-xl border border-status-green/30 bg-status-green/10 px-4 py-3 text-sm text-status-green">
          Konfiguriert ({selected?.label ?? provider}).
        </p>
      ) : null}

      {error ? (
        <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="rounded-xl border border-status-green/30 bg-status-green/10 px-4 py-3 text-sm text-status-green">
          Gespeichert.
        </p>
      ) : null}

      <fieldset className="space-y-4" disabled={!canWrite}>
        <label className="block text-sm">
          <span className="mb-1.5 block font-medium text-label">Anlage / Anbieter</span>
          <select
            className="eh-input"
            value={provider}
            onChange={(e) => setProvider(e.target.value as BarrierProviderKind)}
          >
            {BARRIER_PROVIDER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {selected ? <p className="mt-1 text-xs text-label-secondary">{selected.hint}</p> : null}
        </label>

        {barrierNeedsBaseUrl(provider) ? (
          <label className="block text-sm">
            <span className="mb-1.5 block font-medium text-label">Gateway-URL</span>
            <input
              className="eh-input"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="http://192.168.1.50:8080"
            />
          </label>
        ) : null}

        {barrierNeedsWebhookUrl(provider) ? (
          <label className="block text-sm">
            <span className="mb-1.5 block font-medium text-label">Webhook-URL</span>
            <input
              className="eh-input"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://example.com/campdesk/barrier"
            />
          </label>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1.5 block font-medium text-label">Spur / Lane-ID (optional)</span>
            <input
              className="eh-input"
              value={laneId}
              onChange={(e) => setLaneId(e.target.value)}
              placeholder="entrance-1"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1.5 block font-medium text-label">
              API-Key{apiKeyConfigured ? " (gespeichert)" : ""}
            </span>
            <input
              className="eh-input"
              type="password"
              autoComplete="new-password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={apiKeyConfigured ? "••••••••" : "optional"}
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="mb-1.5 block font-medium text-label">
              Signatur-Secret{apiSecretConfigured ? " (gespeichert)" : ""}
            </span>
            <input
              className="eh-input"
              type="password"
              autoComplete="new-password"
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              placeholder={apiSecretConfigured ? "••••••••" : "optional (HMAC-SHA256)"}
            />
          </label>
        </div>

        <div className="space-y-2 rounded-xl border border-separator p-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="rounded border-separator text-accent focus:ring-accent"
              checked={openOnCheckIn}
              onChange={(e) => setOpenOnCheckIn(e.target.checked)}
            />
            Bei Check-in freischalten / öffnen
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="rounded border-separator text-accent focus:ring-accent"
              checked={revokeOnCheckOut}
              onChange={(e) => setRevokeOnCheckOut(e.target.checked)}
            />
            Bei Check-out sperren
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="rounded border-separator text-accent focus:ring-accent"
              checked={syncWhitelist}
              onChange={(e) => setSyncWhitelist(e.target.checked)}
            />
            Kennzeichen & Transponder in Whitelist synchronisieren
          </label>
        </div>
      </fieldset>

      <div className="flex flex-wrap gap-3">
        <Button type="submit" disabled={!canWrite || saving}>
          {saving ? "Speichern…" : "Speichern"}
        </Button>
        <Button type="button" variant="secondary" disabled={!canWrite || testing} onClick={() => void onTest()}>
          {testing ? "Teste…" : "Verbindung testen"}
        </Button>
      </div>
      {testResult ? <p className="text-sm text-label-secondary">{testResult}</p> : null}
    </form>
  );
}
